import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, subMonths, subQuarters, subYears } from "date-fns";
import { de } from "date-fns/locale";
import { useLanguage } from "../contexts/LanguageContext";
import { formatCurrency } from "../lib/currencies";
import { 
  FileText, 
  Mail, 
  Send, 
  Loader2, 
  Calendar,
  CheckCircle,
  AlertCircle,
  Paperclip,
  Euro,
  Eye,
  Printer,
  Download,
  TrendingUp,
  PieChart,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Period type keys for translation
const PERIOD_TYPE_KEYS = [
  { value: "monthly", key: "reports.monthly" },
  { value: "quarterly", key: "reports.quarterly" },
  { value: "semi_annual", key: "reports.semiAnnual" },
  { value: "annual", key: "reports.annual" }
];

// Generate options based on period type
const generatePeriodOptions = (periodType) => {
  const options = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (periodType === "monthly") {
    for (let i = 0; i < 24; i++) {
      const date = subMonths(now, i);
      options.push({
        value: `${date.getFullYear()}-${date.getMonth() + 1}`,
        label: format(date, "MMMM yyyy", { locale: de }),
        year: date.getFullYear(),
        month: date.getMonth() + 1
      });
    }
  } else if (periodType === "quarterly") {
    for (let year = currentYear; year >= currentYear - 3; year--) {
      for (let q = 4; q >= 1; q--) {
        if (year === currentYear && q > Math.ceil((now.getMonth() + 1) / 3)) continue;
        const quarterNames = { 1: "Q1 (Jan-Mär)", 2: "Q2 (Apr-Jun)", 3: "Q3 (Jul-Sep)", 4: "Q4 (Okt-Dez)" };
        options.push({
          value: `${year}-Q${q}`,
          label: `${quarterNames[q]} ${year}`,
          year: year,
          quarter: q
        });
      }
    }
  } else if (periodType === "semi_annual") {
    for (let year = currentYear; year >= currentYear - 3; year--) {
      for (let h = 2; h >= 1; h--) {
        if (year === currentYear && h === 2 && now.getMonth() < 6) continue;
        const halfNames = { 1: "1. Halbjahr (Jan-Jun)", 2: "2. Halbjahr (Jul-Dez)" };
        options.push({
          value: `${year}-H${h}`,
          label: `${halfNames[h]} ${year}`,
          year: year,
          half: h
        });
      }
    }
  } else if (periodType === "annual") {
    for (let year = currentYear; year >= currentYear - 5; year--) {
      options.push({
        value: `${year}`,
        label: `Jahr ${year}`,
        year: year
      });
    }
  }
  
  return options;
};

export default function ReportsPage() {
  const { language, t } = useLanguage();
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const [periodType, setPeriodType] = useState("monthly");
  const [periodOptions, setPeriodOptions] = useState(generatePeriodOptions("monthly"));
  const [selectedPeriod, setSelectedPeriod] = useState("");
  
  const [formData, setFormData] = useState({
    recipientEmail: "",
    recipientName: "Steuerberater",
    includeAttachments: true,
    notes: ""
  });

  // Update period options when type changes
  useEffect(() => {
    const newOptions = generatePeriodOptions(periodType);
    setPeriodOptions(newOptions);
    setSelectedPeriod(newOptions[1]?.value || newOptions[0]?.value || "");
    setPreviewData(null);
  }, [periodType]);

  // Load preview when period changes
  const loadPreview = async () => {
    if (!selectedPeriod) return;
    
    const selected = periodOptions.find(p => p.value === selectedPeriod);
    if (!selected) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/reports/preview`, {
        year: selected.year,
        period_type: periodType,
        month: selected.month || null,
        quarter: selected.quarter || null,
        half: selected.half || null
      });
      setPreviewData(response.data);
    } catch (error) {
      console.error("Preview error:", error);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPeriod) {
      loadPreview();
    }
  }, [selectedPeriod]);

  const handleSendReport = async (e) => {
    e.preventDefault();
    
    if (!formData.recipientEmail) {
      toast.error("Bitte E-Mail-Adresse des Empfängers eingeben");
      return;
    }
    
    const selected = periodOptions.find(p => p.value === selectedPeriod);
    if (!selected) {
      toast.error("Bitte Zeitraum auswählen");
      return;
    }
    
    setSending(true);
    setLastResult(null);
    
    try {
      const response = await axios.post(`${API}/reports/monthly-tax-report`, {
        year: selected.year,
        month: selected.month || 1,
        recipient_email: formData.recipientEmail,
        recipient_name: formData.recipientName || "Steuerberater",
        include_attachments: formData.includeAttachments,
        notes: formData.notes || null,
        language: language
      });
      
      setLastResult({
        success: true,
        ...response.data
      });
      
      toast.success(response.data.message);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Report konnte nicht gesendet werden";
      setLastResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleExportPrint = async () => {
    const selected = periodOptions.find(p => p.value === selectedPeriod);
    if (!selected) {
      toast.error("Bitte Zeitraum auswählen");
      return;
    }
    
    try {
      // Build query params
      const params = new URLSearchParams({
        year: selected.year,
        period_type: periodType,
        lang: language
      });
      if (selected.month) params.append("month", selected.month);
      if (selected.quarter) params.append("quarter", selected.quarter);
      if (selected.half) params.append("half", selected.half);
      
      // Fetch HTML with authentication
      const response = await axios.get(`${API}/reports/export?${params.toString()}`, {
        responseType: 'text'
      });
      
      // Open in new window and write HTML content
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(response.data);
        printWindow.document.close();
      } else {
        toast.error("Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Export fehlgeschlagen");
    }
  };

  const maxCategoryAmount = previewData?.category_breakdown?.length > 0 
    ? Math.max(...previewData.category_breakdown.map(c => c.amount))
    : 0;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-[Manrope]">{t("reports.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("reports.subtitle")}</p>
      </div>

      {/* Period Selection Card */}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* Period Type */}
            <div className="w-full md:w-48">
              <Label className="text-base font-medium">{t("reports.periodType")}</Label>
              <Select
                value={periodType}
                onValueChange={setPeriodType}
              >
                <SelectTrigger className="mt-2" data-testid="period-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_TYPE_KEYS.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(type.key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Period Selection */}
            <div className="flex-1">
              <Label className="text-base font-medium">{t("reports.selectPeriod")}</Label>
              <Select
                value={selectedPeriod}
                onValueChange={setSelectedPeriod}
              >
                <SelectTrigger className="mt-2" data-testid="period-select">
                  <SelectValue placeholder={t("reports.selectPeriodPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                disabled={!previewData || previewData.expenses_count === 0}
                data-testid="preview-btn"
                className="w-full sm:w-auto"
              >
                <Eye className="h-4 w-4 mr-2" />
                {t("reports.preview")}
              </Button>
              <Button
                variant="default"
                className="bg-[#E31E24] hover:bg-[#c91a1f] w-full sm:w-auto"
                onClick={handleExportPrint}
                disabled={!previewData || previewData.expenses_count === 0}
                data-testid="export-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                {t("reports.exportPrint")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Summary */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">{t("reports.loading")}</p>
          </CardContent>
        </Card>
      ) : previewData && previewData.expenses_count > 0 ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            <Card className="card-hover">
              <CardContent className="p-3 md:p-4 text-center">
                <p className="text-xl md:text-3xl font-bold font-[JetBrains_Mono] text-[#E31E24]">{previewData.expenses_count}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Ausgaben</p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-3 md:p-4 text-center">
                <p className="text-lg md:text-3xl font-bold font-[JetBrains_Mono]">{formatCurrency(previewData.total_eur)}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{t("reports.totalAmount")}</p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-3 md:p-4 text-center">
                <p className="text-lg md:text-3xl font-bold font-[JetBrains_Mono] text-blue-600">{formatCurrency(previewData.fix_total || 0)}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{t("reports.fixCosts")}</p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-3 md:p-4 text-center">
                <p className="text-lg md:text-3xl font-bold font-[JetBrains_Mono] text-green-600">{formatCurrency(previewData.variable_total || 0)}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{t("reports.variableCosts")}</p>
              </CardContent>
            </Card>
            <Card className="card-hover col-span-2 sm:col-span-1">
              <CardContent className="p-3 md:p-4 text-center">
                <p className="text-xl md:text-3xl font-bold font-[JetBrains_Mono]">{previewData.attachments_count}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{t("reports.receipts")}</p>
              </CardContent>
            </Card>
          </div>

          {/* Statistics Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Breakdown */}
            {previewData.category_breakdown && previewData.category_breakdown.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PieChart className="h-5 w-5 text-[#E31E24]" />
                    {t("reports.byCategory")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {previewData.category_breakdown.slice(0, 6).map((cat, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="truncate max-w-[200px]">{cat.name}</span>
                        <span className="font-mono font-medium">{formatCurrency(cat.amount)}</span>
                      </div>
                      <Progress 
                        value={(cat.amount / maxCategoryAmount) * 100} 
                        className="h-2"
                        style={{ "--progress-color": cat.color }}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Monthly Trend (for non-monthly reports) */}
            {periodType !== "monthly" && previewData.monthly_trend && previewData.monthly_trend.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="h-5 w-5 text-[#E31E24]" />
                    {t("reports.monthlyTrend")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2 h-32">
                    {previewData.monthly_trend.map((m, idx) => {
                      const maxTotal = Math.max(...previewData.monthly_trend.map(t => t.total));
                      const height = maxTotal > 0 ? (m.total / maxTotal) * 100 : 0;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-gradient-to-t from-[#E31E24] to-[#ff6b6b] rounded-t"
                            style={{ height: `${height}%`, minHeight: '4px' }}
                          />
                          <span className="text-xs font-mono mt-1">{Math.round(m.total)}€</span>
                          <span className="text-xs text-muted-foreground">{m.month_name}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Vendors */}
            {previewData.top_vendors && previewData.top_vendors.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="h-5 w-5 text-[#E31E24]" />
                    {t("reports.topVendors")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {previewData.top_vendors.slice(0, 5).map((vendor, idx) => (
                      <div key={idx} className="flex justify-between items-center py-1 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground w-5">{idx + 1}.</span>
                          <span className="text-sm truncate max-w-[180px]">{vendor.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono text-sm font-medium">{formatCurrency(vendor.amount)}</span>
                          <span className="text-xs text-muted-foreground ml-2">({vendor.count}x)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Currency Breakdown */}
            {previewData.total_by_currency && Object.keys(previewData.total_by_currency).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Euro className="h-5 w-5 text-[#E31E24]" />
                    {t("reports.byCurrency")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(previewData.total_by_currency).map(([curr, amt]) => (
                      <div key={curr} className="bg-muted rounded-lg px-4 py-3 text-center">
                        <p className="text-xl font-bold font-[JetBrains_Mono]">{formatCurrency(amt, curr)}</p>
                        <p className="text-xs text-muted-foreground">{curr}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      ) : previewData ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t("reports.noData")}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Email Tab */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#E31E24]" />
            {t("reports.sendEmail")}
          </CardTitle>
          <CardDescription>
            {t("reports.sendDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendReport} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">{t("reports.recipientEmail")} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.recipientEmail}
                  onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                  placeholder="steuerberater@beispiel.de"
                  required
                  data-testid="recipient-email"
                />
              </div>
              <div>
                <Label htmlFor="name">{t("reports.recipientName")}</Label>
                <Input
                  id="name"
                  value={formData.recipientName}
                  onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
                  placeholder="z.B. Herr Müller"
                  data-testid="recipient-name"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="attachments" className="cursor-pointer">{t("reports.includeAttachments")}</Label>
                <p className="text-sm text-muted-foreground">{t("reports.allInvoicesAsFiles")}</p>
              </div>
              <Switch
                id="attachments"
                checked={formData.includeAttachments}
                onCheckedChange={(checked) => setFormData({ ...formData, includeAttachments: checked })}
              />
            </div>

            <div>
              <Label htmlFor="notes">{t("reports.notes")}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t("reports.additionalNotes")}
                rows={2}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#E31E24] hover:bg-[#c91a1f]"
              disabled={sending || !previewData || previewData.expenses_count === 0}
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("reports.sending")}</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />{t("reports.sendReport")}</>
              )}
            </Button>
          </form>

          {lastResult && (
            <div className={`mt-4 p-4 rounded-lg ${lastResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              <div className="flex items-center gap-2">
                {lastResult.success ? <CheckCircle className="h-5 w-5 text-green-500" /> : <AlertCircle className="h-5 w-5 text-red-500" />}
                <span className={`font-medium ${lastResult.success ? "text-green-700" : "text-red-700"}`}>
                  {lastResult.success ? t("reports.sent") : t("reports.error")}
                </span>
              </div>
              <p className="text-sm mt-1">{lastResult.message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg">Vorschau: {previewData?.period_label}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewData && previewData.expenses && previewData.expenses.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 mb-4">
                  <div className="p-2 md:p-3 bg-muted rounded-lg text-center">
                    <p className="text-lg md:text-xl font-bold">{previewData.expenses_count}</p>
                    <p className="text-xs text-muted-foreground">Ausgaben</p>
                  </div>
                  <div className="p-2 md:p-3 bg-muted rounded-lg text-center">
                    <p className="text-sm md:text-xl font-bold">{formatCurrency(previewData.total_eur)}</p>
                    <p className="text-xs text-muted-foreground">Gesamt</p>
                  </div>
                  <div className="p-2 md:p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm md:text-xl font-bold text-blue-600">{formatCurrency(previewData.fix_total || 0)}</p>
                    <p className="text-xs text-muted-foreground">Fix</p>
                  </div>
                  <div className="p-2 md:p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-sm md:text-xl font-bold text-green-600">{formatCurrency(previewData.variable_total || 0)}</p>
                    <p className="text-xs text-muted-foreground">Variable</p>
                  </div>
                  <div className="p-2 md:p-3 bg-muted rounded-lg text-center col-span-2 sm:col-span-1">
                    <p className="text-lg md:text-xl font-bold">{previewData.attachments_count}</p>
                    <p className="text-xs text-muted-foreground">Belege</p>
                  </div>
                </div>
                
                <div className="overflow-x-auto -mx-2 md:mx-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">Nr.</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="hidden sm:table-cell">Titel</TableHead>
                      <TableHead className="sm:hidden">Info</TableHead>
                      <TableHead className="hidden md:table-cell">Rechnungsnr.</TableHead>
                      <TableHead className="hidden lg:table-cell">Anbieter</TableHead>
                      <TableHead className="hidden md:table-cell">Kategorie</TableHead>
                      <TableHead className="text-right">Betrag</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">EUR</TableHead>
                      <TableHead className="text-center w-12 hidden sm:table-cell">Beleg</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.expenses.map((exp, idx) => (
                      <TableRow key={exp.id || idx}>
                        <TableCell className="text-xs">{idx + 1}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{exp.date}</TableCell>
                        <TableCell className="hidden sm:table-cell font-medium max-w-[150px] truncate text-sm">{exp.title}</TableCell>
                        <TableCell className="sm:hidden text-xs">
                          <div className="truncate max-w-[100px] font-medium">{exp.title}</div>
                          <div className="text-muted-foreground">{exp.vendor || "-"}</div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs">{exp.invoice_number || "-"}</TableCell>
                        <TableCell className="hidden lg:table-cell max-w-[120px] truncate text-xs">{exp.vendor || "-"}</TableCell>
                        <TableCell className="hidden md:table-cell text-xs">{exp.category_name}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatCurrency(exp.amount, exp.currency)}</TableCell>
                        <TableCell className="text-right font-mono text-xs hidden sm:table-cell">{formatCurrency(exp.amount_eur || exp.amount)}</TableCell>
                        <TableCell className="text-center hidden sm:table-cell">
                          {exp.has_attachment ? <Paperclip className="h-4 w-4 mx-auto text-green-500" /> : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-[#E31E24] text-white font-bold">
                      <TableCell colSpan={2} className="text-right sm:hidden">Gesamt:</TableCell>
                      <TableCell colSpan={7} className="text-right hidden sm:table-cell">Gesamtsumme:</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(previewData.total_eur)}</TableCell>
                      <TableCell className="hidden sm:table-cell"></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                </div>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">Keine Ausgaben vorhanden</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Schließen</Button>
            <Button onClick={handleExportPrint} className="bg-[#E31E24] hover:bg-[#c91a1f]">
              <Printer className="h-4 w-4 mr-2" />Exportieren / Drucken
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
